/* eslint-disable no-prototype-builtins */

import { AppUserType } from './app-user-type.enum';
import { Checkpoints } from './checkpoints.interface';
import { Guard } from './guard';

export interface CmRouteCheckpoint {
	some?: Checkpoints;
	none?: Checkpoints;
	every?: Checkpoints;
}

type RouteChecks = Omit<Checkpoints, 'userType'> & { userType?: AppUserType };

export abstract class CmRouteGuard {
	constructor() {}

	protected passesGuardCheck(guard: Guard, data: any): boolean {
		if (!data) {
			return true;
		}

		data = data.guard || data;
		
		if (data.custom) {
			return data.custom(guard);
		}

		const { every, some, none } = this.parseRouteData(data);
		let passes = true;

		passes = every ? this.passesEveryCheckpoint(guard, every) : passes;
		passes = passes && some ? this.passesSomeCheckpoints(guard, some) : passes;
		passes = passes && none ? !this.passesSomeCheckpoints(guard, none) : passes;

		return passes;
	}

	private passesEveryCheckpoint(
		guard: Guard,
		{ features, permissions, admin, internal, qlik, custom }: RouteChecks
	): boolean {
		guard.reset();

		if (features && features.length) {
			guard = guard.everyFeature(features);
		}

		if (permissions && permissions.length) {
			guard = guard.everyPermission(permissions);
		}

		if (admin) {
			guard = guard.isAdmin();
		}

		if (internal) {
			guard = guard.isInternal();
		}

		if (qlik) {
			guard = guard.hasQlik();
		}

		return guard.all;
	}

	private passesSomeCheckpoints(
		guard: Guard,
		{ features, permissions, admin, internal, qlik, custom }: RouteChecks
	): boolean {
		guard.reset();

		if (features && features.length) {
			guard = guard.someFeatures(features);
		}

		if (permissions && permissions.length) {
			guard = guard.somePermissions(permissions);
		}

		if (admin) {
			guard = guard.isAdmin();
		}

		if (internal) {
			guard = guard.isInternal();
		}

		if (qlik) {
			guard = guard.hasQlik();
		}

		return guard.any;
	}

	private parseRouteData(data: any) {
		const guard = (data || {}).guard || {};
		const every = {
			features: [...(data?.features || []), ...((guard.every || {}).features || [])],
			permissions: [...(data?.permissions || []), ...((guard.every || {}).permissions || [])],
		};

		if (data?.hasOwnProperty('admin')) {
			every['admin'] = data.admin;
		}

		if (guard.every && data.guard.every.hasOwnProperty('admin')) {
			every['admin'] = data.guard.every.admin;
		}

		if (data?.hasOwnProperty('internal')) {
			every['internal'] = data.internal;
		}

		if (guard.every && data.guard.every.hasOwnProperty('internal')) {
			every['internal'] = data.guard.every.internal;
		}

		if (data?.hasOwnProperty('qlik')) {
			every['qlik'] = data.qlik;
		}

		if (guard.every && data.guard.every.hasOwnProperty('qlik')) {
			every['qlik'] = data.guard.every.qlik;
		}

		if (data?.hasOwnProperty('custom')) {
			every['custom'] = data.custom;
		}

		if (guard.every && data.guard.every.hasOwnProperty('custom')) {
			every['custom'] = data.guard.every.custom;
		}

		const { some, none } = guard;

		return { some, none, every };
	}
}
